import React, { useState, useEffect } from "react";
import nearbyData from "../data/jsons/__nearby.json";
import {
  Table,
  Modal,
  Carousel,
  Button,
  Spin,
  Typography,
  Divider,
  Tag,
  Empty,
  Card,
  List,
} from "antd";
import {
  PhoneOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
  ShopOutlined,
} from "@ant-design/icons";
// import "antd/dist/antd.css";
import "./Nearby.css";

const { Title, Text } = Typography;

const Nearby = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processedData, setProcessedData] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Process Google Drive links to direct image URLs
  const processGoogleDriveLink = (link) => {
    if (!link) return null;
    const fileId = link.match(/[-\w]{25,}/);
    return fileId
      ? `https://drive.google.com/uc?export=view&id=${fileId[0]}`
      : null;
  };

  useEffect(() => {
    // Add window resize listener
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Process the data and add keys for the table
    const processed = nearbyData.map((item, index) => ({
      ...item,
      key: index,
      processedPhotos: [
        item["Photo 1"],
        item["Photo 2"],
        item["Photo 3"],
      ].filter(Boolean),
    }));

    setProcessedData(processed);
    setLoading(false);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const showModal = (record) => {
    setSelectedPlace(record);

    // Prepare image URLs for the carousel
    const urls = [
      record.processedPhotos[0],
      record.processedPhotos[1],
      record.processedPhotos[2],
    ].filter(Boolean);

    setImageUrls(urls);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const openDirections = () => {
    if (selectedPlace && selectedPlace.Maps) {
      // Destination coordinates
      const destination = "15.453444,75.292778";

      // Extract source coordinates if available
      let source = "";
      if (selectedPlace.Maps.includes("maps?q=")) {
        const coords = selectedPlace.Maps.split("maps?q=")[1].split(",");
        if (coords.length === 2) {
          source = `${coords[0]},${coords[1]}`;
        }
      }

      // Create directions URL
      const directionsUrl = `https://www.google.com/maps/dir/${destination}/${source}`;
      window.open(directionsUrl, "_blank");
    }
  };

  const columns = [
    {
      title: "Shop Name",
      dataIndex: "Shop Name",
      key: "shopName",
      render: (text) => <span className="shop-name">{text}</span>,
    },
    {
      title: "Tags",
      dataIndex: "Other name",
      key: "type",
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : "-"),
    },
    {
      title: "Timings",
      dataIndex: "Timings",
      key: "timings",
      render: (text) =>
        text ? (
          <span>
            <ClockCircleOutlined /> {text}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Contact",
      dataIndex: "Mobile",
      key: "mobile",
      render: (text) =>
        text ? (
          <span>
            <PhoneOutlined /> {text}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => showModal(record)}
          className="view-details-btn"
        >
          View Details
        </Button>
      ),
    },
  ];

  // Mobile card view component
  const MobileCardView = () => (
    <div className="mobile-card-view">
      <List
        dataSource={processedData}
        renderItem={(item) => (
          <div className="place-card" onClick={() => showModal(item)}>
            <div className="place-card-header">
              <div>
                <div className="place-card-title">{item["Shop Name"]}</div>
                {item["Other name"] && (
                  <div className="place-card-subtitle">
                    <Tag color="blue">{item["Other name"]}</Tag>
                  </div>
                )}
              </div>
              {item.processedPhotos.length > 0 && (
                <img
                  src={item.processedPhotos[0]}
                  alt={item["Shop Name"]}
                  className="place-card-thumbnail"
                />
              )}
            </div>
            <div className="place-card-details">
              {item["Timings"] && (
                <div className="place-card-detail">
                  <ClockCircleOutlined />
                  <span>{item["Timings"]}</span>
                </div>
              )}
              {item["Mobile"] && (
                <div className="place-card-detail">
                  <PhoneOutlined />
                  <span>{item["Mobile"]}</span>
                </div>
              )}
              {item["Maps"] && (
                <div className="place-card-detail">
                  <EnvironmentOutlined />
                  <span>View on map</span>
                </div>
              )}
            </div>
            <Button
              type="primary"
              size="small"
              block
              className="view-details-btn"
            >
              View Details
            </Button>
          </div>
        )}
      />
    </div>
  );

  return (
    <div className="nearby-container">
      <div className="nearby-header">
        <Title level={2}>Nearby Places & Services</Title>
        <Text type="secondary">
          Discover shops, restaurants, and essential services in the vicinity
        </Text>
      </div>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading nearby places...</Text>
        </div>
      ) : (
        <>
          <div className="table-container">
            <Table
              columns={columns}
              dataSource={processedData}
              pagination={{ pageSize: 10 }}
              rowClassName="table-row"
              onRow={(record) => ({
                onClick: () => showModal(record),
              })}
            />
          </div>

          <MobileCardView />
        </>
      )}

      {selectedPlace && (
        <Modal
          title={
            <div className="modal-title">
              <span>{selectedPlace["Shop Name"]}</span>
              {selectedPlace["Other name"] && (
                <Tag color="blue" className="modal-tag">
                  {selectedPlace["Other name"]}
                </Tag>
              )}
            </div>
          }
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={[
            <Button key="back" onClick={handleCancel}>
              Close
            </Button>,
          ]}
          width={windowWidth > 768 ? 800 : "100%"}
          className="place-modal"
          centered={windowWidth <= 768}
          destroyOnClose={true}
        >
          <div className="modal-content">
            {imageUrls.length > 0 ? (
              <div className="carousel-container">
                <Carousel autoplay>
                  {imageUrls.map((url, index) => (
                    <div key={index} className="carousel-item">
                      <img
                        src={url}
                        alt={`${selectedPlace["Shop Name"]} - ${index + 1}`}
                      />
                    </div>
                  ))}
                </Carousel>
              </div>
            ) : (
              <Empty
                description="No images available"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="no-images"
              />
            )}

            <Divider />

            <div className="place-details">
              <div className="detail-item">
                <InfoCircleOutlined className="detail-icon" />
                <div>
                  <Text strong>Description: </Text>
                  <Text>
                    {selectedPlace["Other name"] || "No description available"}
                  </Text>
                </div>
              </div>

              {selectedPlace["Timings"] && (
                <div className="detail-item">
                  <ClockCircleOutlined className="detail-icon" />
                  <div>
                    <Text strong>Opening Hours: </Text>
                    <Text>{selectedPlace["Timings"]}</Text>
                  </div>
                </div>
              )}

              {selectedPlace["Mobile"] && (
                <div className="detail-item">
                  <PhoneOutlined className="detail-icon" />
                  <div>
                    <Text strong>Contact: </Text>
                    <Text>
                      <a href={`tel:${selectedPlace["Mobile"]}`}>
                        {selectedPlace["Mobile"]}
                      </a>
                    </Text>
                  </div>
                </div>
              )}

              {selectedPlace["Maps"] && (
                <div className="map-container">
                  <Text strong className="map-title">
                    Location
                  </Text>
                  <iframe
                    title={`Map - ${selectedPlace["Shop Name"]}`}
                    src={`https://maps.google.com/maps?q=${
                      selectedPlace["Maps"].split("maps?q=")[1]
                    }&output=embed`}
                    width="100%"
                    height={windowWidth <= 768 ? "250" : "300"}
                    frameBorder="0"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    aria-hidden="false"
                    tabIndex="0"
                  />
                  <Button
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    onClick={openDirections}
                    className="directions-btn"
                  >
                    Get Directions to Our Location
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Nearby;
